import React, { useEffect, useState } from "react";

import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import type { PageProps } from "../../types/page.ts";
import { OverviewContent } from "../../components/features/time-allocated-overview/components/overview-content/overview-content.tsx";
import { OverviewToolbar } from "../../components/features/time-allocated-overview/components/overview-toolbar/overview-toolbar.tsx";
import type { Period, PeriodRange } from "../../components/features/time-allocated-overview/types.ts";
import { defaultRangeForPeriod } from "../../utils/period-range.ts";
import { useEmployeesList } from "../../hooks/time-allocated-overview/use-employees-list.ts";
import { useTimeAllocatedStats } from "../../hooks/time-allocated-overview/use-time-allocated-stats.ts";

import styles from "./time-allocated-overview.module.css";

const PAGE_TITLE = "Employee Overview Report";

const TimeAllocatedOverview = ({ title }: PageProps) => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const pageTitle = title?.trim() || PAGE_TITLE;

  const [employeeId, setEmployeeId] = useState("");
  const [period, setPeriod] = useState<Period>("MONTHLY");
  /** Default: current calendar month (first–last day). */
  const [range, setRange] = useState<PeriodRange>(() =>
    defaultRangeForPeriod("MONTHLY"),
  );

  const handlePeriodChange = (p: Period) => {
    setPeriod(p);
    setRange(defaultRangeForPeriod(p));
  };

  const {
    options: employeeOptions,
    loading: employeesLoading,
    error: employeesError,
  } = useEmployeesList();

  const { data, loading, error } = useTimeAllocatedStats(
    employeeId,
    range,
  );

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, pageTitle]);

  return (
    <PageShell className={styles.shell}>
      <OverviewToolbar
        employeeId={employeeId}
        onEmployeeIdChange={setEmployeeId}
        employeeOptions={employeeOptions}
        employeesLoading={employeesLoading}
        employeesError={employeesError}
        period={period}
        onPeriodChange={handlePeriodChange}
        range={range}
        onRangeChange={setRange}
      />
      <OverviewContent
        range={range}
        loading={loading}
        error={error}
        data={data}
        employeeId={employeeId}
      />
    </PageShell>
  );
};

export default TimeAllocatedOverview;
